import ExcelJS from "exceljs";
import { toast } from "react-toastify";

export const exportTablesToExcel = async (
  dataToExport: any,
  headerNames: string[],
  customNameToSave: string,
  rowsBeforeHeaderInExcel: any,
  setLoading?: (loading: boolean) => void
) => {
  setLoading && setLoading(true);

  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    // Add the rows before the header
    rowsBeforeHeaderInExcel.forEach((row: any, index: number) => {
      const dataRow = worksheet.addRow(row.data);
      dataRow.font = { bold: row.isBold, size: row.fontSize };

      // Merge cells to span the whole row
      worksheet.mergeCells(`A${dataRow.number}:Z${dataRow.number}`);

      // Adjust the row height based on the font size
      const lineHeight = 1.4; // Set the line height factor

      const rowHeight = row.fontSize * lineHeight;
      dataRow.height = rowHeight;

      if (row?.emptyRowsUnder) {
        for (let i = 0; i < row.emptyRowsUnder; i++) {
          worksheet.addRow([]);
        }
      }
    });

    worksheet.addRow([]); // Add an empty row for the header names

    const rowsBeforeHeader =
      rowsBeforeHeaderInExcel.length +
      3 +
      (rowsBeforeHeaderInExcel?.reduce((acc: number, curr: any) => acc + (curr?.emptyRowsUnder ?? 0), 0) ?? 0);

    // Apply styles to the header row
    const tableStartsFromRow = rowsBeforeHeader;
    const headerRow = worksheet.getRow(tableStartsFromRow);

    headerNames.forEach((header, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = header ? header : ""; // Set the header name in the respective cell
      cell.font = { bold: true, color: { argb: "FFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4c6492" },
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };

      const numberCell = worksheet.getCell(tableStartsFromRow + 1, index + 1);

      numberCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4c6492" },
      };
      numberCell.border = {
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      const rowNumber = headerRow.number;
      const requiredRowHeight = getRowHeight(headerRow, 13);
      worksheet.getRow(rowNumber).height = requiredRowHeight * 2.5;
    });

    // Set border styling for header row
    headerRow.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Set data rows
    for (const row of dataToExport) {
      const rowData = headerNames.map((el: string) => {
        return row[el].value;
      });
      const rowStyle = headerNames.map((el: string) => {
        return row[el].style;
      });
      const dataRow = worksheet.addRow(rowData);
      dataRow.alignment = { wrapText: true, horizontal: "center" };

      dataRow.eachCell((cell, index) => {
        cell.alignment = {
          wrapText: true,
          horizontal: rowStyle[index - 1]?.horizontalAlign ?? "center",
          vertical: "middle",
        };
        cell.font = {
          size: rowStyle[index - 1]?.fontSize,
          bold: !!rowStyle[index - 1]?.bold,
          strike: !!rowStyle[index - 1]?.strike,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        if (rowStyle[index - 1]?.backGroundColor) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: rowStyle[index - 1].backGroundColor },
          };
        }
      });

      // Resize row height based on the wrapped text
      const rowNumber = dataRow.number;
      const requiredRowHeight = getRowHeight(dataRow);
      worksheet.getRow(rowNumber).height = requiredRowHeight * 1.5;
    }

    // Auto fit column widths
    worksheet.columns.forEach((column) => {
      column.width = column?.header?.length ?? 0 < 17 ? 17 : column?.header?.length ?? 0;
    });

    // Create a buffer containing the Excel file
    const buffer = await workbook.xlsx.writeBuffer();

    // Create a Blob from the buffer
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

    // Create a download link element
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${customNameToSave}.xlsx`;
    link.click();
  } catch (error) {
    console.error("Error generating file:", error);

    toast.error("Something went wrong while generating file.", { autoClose: 10000 });

    setLoading && setLoading(false);
  } finally {
    setLoading && setLoading(false);
  }
};

const calculateCellValueHeight = (text: string, cellWidth: number, fontSize: number) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (context) {
    context.font = `${fontSize}px Arial`;

    const averageCharacterWidth = context.measureText("a").width;
    const estimatedCharacterWidth = cellWidth * averageCharacterWidth;

    const lines = text.split("\n");

    let maxHeight = 0;
    const result: number[] = [];

    lines.forEach((line) => {
      maxHeight = 0;
      const words = line.split(" ");
      let currentLine = "";
      let lineCount = 0;

      words.forEach((word) => {
        const testLine = `${currentLine + word} `;
        const lineWidth = context.measureText(testLine).width;

        if (lineWidth > estimatedCharacterWidth && currentLine.trim() !== "") {
          lineCount++;
          currentLine = `${word} `;
        } else {
          currentLine = testLine;
        }
      });

      if (currentLine.trim() !== "") {
        lineCount++;
      }

      maxHeight = Math.max(maxHeight, lineCount * fontSize);
      result.push(maxHeight);
    });

    return result.reduce((acc, curr) => acc + curr, 0);
  }
};

// Estimate the required row height based on the wrapped text
const getRowHeight = (row: any, fontSize?: number) => {
  const defaultRowHeight = 17; // Set a default row height
  const defaultFontSize = 11; // Set a default font size

  let maxHeight = defaultRowHeight;

  row.eachCell((cell: any, cellNumber: number) => {
    const wrapText = cell.alignment && cell.alignment.wrapText;
    const text = cell.text || "";

    if (wrapText) {
      const cellHeight = calculateCellValueHeight(text, 17, fontSize ?? defaultFontSize);
      maxHeight = Math.max(maxHeight, cellHeight as number);
    }
  });

  return maxHeight;
};
